import { useQuery } from '@apollo/client';
import Link from 'next/link';
import Tooltip from '@mui/material/Tooltip';
import BadgeArt from '../Badges/BadgeArt';
import { checkBadgeHidden, getBadge } from '../../helpers/getBadge';
import { GET_BADGES } from '../../helpers/graphql/profile';

// Achievements as passport stamps: tilted, dashed-border tiles in
// alternating inks, in ONE horizontally scrollable row so a well-traveled
// author's passport never crowds out their posts. Tilts cycle
// deterministically by index so server and client render the same page.
const TILTS = [
  '-tw-rotate-2',
  'tw-rotate-1',
  'tw-rotate-2',
  '-tw-rotate-1',
  'tw-rotate-3',
  '-tw-rotate-3',
];

// Each ink also sets text color: the engraved BadgeArt is an alpha mask
// filled with currentColor, so the artwork prints in the tile's ink.
const INKS = [
  'tw-border-primary-700/40 tw-bg-primary-600/5 tw-text-primary-800 dark:tw-border-primary-300/40 dark:tw-bg-primary-300/10 dark:tw-text-primary-300',
  'tw-border-amber-700/40 tw-bg-amber-500/5 tw-text-amber-800 dark:tw-border-amber-500/40 dark:tw-bg-amber-500/10 dark:tw-text-amber-500',
];

function Stamp({ user, type, name, index }) {
  const badge = getBadge(type, name);
  if (!badge || !badge.name) return null;

  const label =
    type === 'region' || type === 'country'
      ? `Blogged about ${badge.name}`
      : badge.description || badge.name;

  return (
    <Tooltip title={label} arrow placement="top">
      <Link
        href={`/@${user}/badges/${type}/${name}`}
        className={`${TILTS[index % TILTS.length]} ${
          INKS[index % INKS.length]
        } tw-flex tw-h-14 tw-w-14 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border-2 tw-border-dashed tw-no-underline tw-transition-transform tw-duration-200 hover:tw-rotate-0 hover:-tw-translate-y-1`}
      >
        <span className="tw-relative tw-block tw-h-8 tw-w-8" aria-hidden="true">
          <BadgeArt badge={badge} type={type} />
        </span>
      </Link>
    </Tooltip>
  );
}

export default function BadgeCard({ user }) {
  const { data, loading } = useQuery(GET_BADGES, { variables: { user } });

  const badges = (data && data.getBadges) || [];
  const visible = badges.filter(
    badge => !checkBadgeHidden(badge.type, badge.name, badges),
  );

  if (!loading && visible.length === 0) return null;

  // Loading skeleton mirrors the loaded strip's exact height — the post
  // grid below must not jump when the stamps arrive.
  if (loading) {
    return (
      <section className="tw-mb-8">
        <div className="tw-mb-3 tw-h-3 tw-w-28 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
        <div className="tw-flex tw-gap-3 tw-py-1">
          {[0, 1, 2, 3, 4, 5].map(i => (
            <div
              key={i}
              className="tw-h-14 tw-w-14 tw-shrink-0 tw-animate-pulse tw-rounded-xl tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]"
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="tw-mb-8">
      <p className="tw-mb-3 tw-font-mono tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.25em] tw-text-[#5F6F69] dark:tw-text-[#93A69E]">
        Passport · {visible.length} stamp{visible.length === 1 ? '' : 's'}
      </p>
      <div className="tw-relative">
        <div className="tw-flex tw-gap-3 tw-overflow-x-auto tw-py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden">
          {visible.map((badge, i) => (
            <Stamp
              key={`${badge.type}-${badge.name}`}
              user={user}
              type={badge.type}
              name={badge.name}
              index={i}
            />
          ))}
        </div>
        {/* Right-edge fade hints that the passport keeps going */}
        <div
          aria-hidden="true"
          className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-w-12 tw-bg-gradient-to-l tw-from-[#FAF8F1] tw-to-transparent dark:tw-from-[#0E1A16]"
        />
      </div>
    </section>
  );
}
